import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppPagination from '../../../components/AppPagination';
import AppActionMenu from '../../../components/AppActionMenu';
import { useCoursesListScreen } from './hooks/useCoursesListScreen';

export default function CoursesListScreen() {
  const { state, actions } = useCoursesListScreen();
  const navigate = useNavigate();

  const statusTabs = [
    { key: 'active', label: 'Active' },
    { key: 'archived', label: 'Archived' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner with Title on Left, Status Toggle Filter on Right */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Education</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">Academic Masters</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Courses Master
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Configure academic degree programs, courses, sections, and department ownership.
          </p>
        </div>

        {/* Actions Cluster: Segmented Status Tabs (Utilizes global top navbar '+ Create New' button) */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.setViewMode(tab.key)}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                  state.viewMode === tab.key
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Filter Row: Search & Reset */}
      <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <label className="relative w-full max-w-md">
          <span className="material-symbols-outlined pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-outline text-lg">
            search
          </span>
          <input
            type="text"
            value={state.searchText}
            onChange={(event) => actions.setSearchText(event.target.value)}
            placeholder="Search by course name or short name..."
            className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 pl-10 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
          />
        </label>

        {state.searchText && (
          <button
            type="button"
            onClick={actions.resetFilters}
            className="inline-flex items-center gap-1 rounded-xl bg-surface-container px-4 py-2.5 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-sm">filter_alt_off</span>
            <span>Clear Filter</span>
          </button>
        )}
      </section>

      {state.error && (
        <div className="rounded-xl border border-error/30 bg-error-container/20 px-4 py-3 text-xs text-error font-medium">
          {state.error}
        </div>
      )}

      {/* Data Table Section */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-outline-variant/20 bg-surface-container-lowest text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
              <tr>
                <th className="py-3.5 pl-6 pr-4">Course Details</th>
                <th className="px-4 py-3.5">Short Name</th>
                <th className="px-4 py-3.5">Department</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="py-3.5 pl-4 pr-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10 text-on-surface">
              {state.isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <span className="text-xs font-medium">Loading courses...</span>
                    </div>
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-4xl text-outline-variant">school</span>
                      <p className="text-sm font-bold text-on-surface">No courses found</p>
                      <p className="text-xs">Try adjusting your search criteria or add a new course.</p>
                      {state.searchText && (
                        <button
                          type="button"
                          onClick={actions.resetFilters}
                          className="mt-2 text-xs font-bold text-primary hover:underline cursor-pointer"
                        >
                          Reset search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/masters/courses/entry?id=${row.id}`)}
                    className="hover:bg-surface-container-low/50 cursor-pointer transition-colors"
                  >
                    <td className="py-3.5 pl-6 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          <span className="material-symbols-outlined text-lg">school</span>
                        </div>
                        <div>
                          <p className="font-bold text-on-surface text-xs leading-snug">{row.name}</p>
                          <p className="text-[11px] text-on-surface-variant">
                            {row.company_name || 'Academic Program'}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3.5 font-bold text-on-surface">
                      {row.short_name || '—'}
                    </td>

                    <td className="px-4 py-3.5 text-on-surface-variant">
                      {row.department_name || 'General / All'}
                    </td>

                    <td className="px-4 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                          row.status === 'Active'
                            ? 'bg-emerald-500/10 text-emerald-600'
                            : 'bg-surface-variant text-on-surface-variant'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            row.status === 'Active' ? 'bg-emerald-500' : 'bg-outline'
                          }`}
                        />
                        {row.status}
                      </span>
                    </td>

                    <td className="py-3.5 pl-4 pr-6 text-right" onClick={(e) => e.stopPropagation()}>
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant hover:bg-surface-container-low hover:text-primary transition bg-transparent cursor-pointer"
                        items={[
                          { key: 'edit', label: 'Edit Course', icon: 'edit' },
                          {
                            key: 'archive',
                            label: row.status === 'Active' ? 'Archive Course' : 'Restore Course',
                            icon: row.status === 'Active' ? 'archive' : 'unarchive',
                          },
                          { key: 'delete', label: 'Delete Course', icon: 'delete', danger: true },
                        ]}
                        onSelect={(key) => {
                          if (key === 'edit') {
                            navigate(`/masters/courses/entry?id=${row.id}`);
                          } else if (key === 'archive') {
                            actions.handleArchive(row.id);
                          } else if (key === 'delete') {
                            actions.handleDelete(row.id);
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Standard Pagination Footer */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  );
}
